import { useEffect, useState } from "react";
import { Loader2, Check } from "lucide-react";
import { adminApi } from "../../lib/adminApi";

const empty = {
  banner_text: { fr: "", nl: "" },
  modal_title: { fr: "", nl: "" },
  modal_body: { fr: "", nl: "" },
  cta_label: { fr: "", nl: "" },
  cta_url: "",
  is_active: true,
};

const field = "w-full rounded-xl bg-ink border border-white/12 px-4 py-2.5 text-sm text-white placeholder:text-white/25 focus:border-accent-yellow focus:outline-none transition-colors";
const lbl = "block text-xs uppercase tracking-[0.2em] text-white/45 mb-2";

const BilingualRow = ({ label, k, form, setLang, textarea, placeholder }) => (
  <div className="grid md:grid-cols-2 gap-5">
    {["fr", "nl"].map((l) => (
      <div key={l}>
        <label className={lbl}>{label} ({l.toUpperCase()})</label>
        {textarea ? (
          <textarea rows={3} className={field} data-testid={`ann-${k}-${l}`} value={form[k][l]} onChange={(e) => setLang(k, l, e.target.value)} placeholder={placeholder} />
        ) : (
          <input className={field} data-testid={`ann-${k}-${l}`} value={form[k][l]} onChange={(e) => setLang(k, l, e.target.value)} placeholder={placeholder} />
        )}
      </div>
    ))}
  </div>
);

export const AnnouncementPanel = () => {
  const [form, setForm] = useState(empty);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);

  useEffect(() => {
    adminApi
      .getAnnouncement()
      .then((d) => {
        if (d) setForm({ ...empty, ...d });
      })
      .catch(() => {})
      .finally(() => setLoading(false));
  }, []);

  const setLang = (k, l, v) => setForm((f) => ({ ...f, [k]: { ...f[k], [l]: v } }));

  const save = async () => {
    setSaving(true);
    setSaved(false);
    try {
      const { banner_text, modal_title, modal_body, cta_label, cta_url, is_active } = form;
      await adminApi.saveAnnouncement({ banner_text, modal_title, modal_body, cta_label, cta_url, is_active });
      setSaved(true);
      setTimeout(() => setSaved(false), 2500);
    } finally {
      setSaving(false);
    }
  };

  if (loading) return <div className="flex items-center gap-2 text-white/50 text-sm"><Loader2 size={16} className="animate-spin" /> Chargement…</div>;

  return (
    <div data-testid="announcement-panel">
      <div className="flex items-center justify-between mb-6">
        <h2 className="font-display text-2xl font-medium">Annonce</h2>
        <label className="inline-flex items-center gap-2.5 text-sm text-white/70 cursor-pointer" data-testid="ann-active-toggle">
          <input type="checkbox" checked={form.is_active} onChange={(e) => setForm((f) => ({ ...f, is_active: e.target.checked }))} className="w-4 h-4 accent-[color:var(--accent-yellow,#FFCC00)]" />
          Annonce active
        </label>
      </div>

      <p className="text-sm text-white/45 mb-6 max-w-2xl leading-relaxed">
        Le texte de la bannière s'affiche dans le bandeau fin en haut du site. Au clic, la fenêtre affiche le titre, le message et un bouton optionnel. Désactivez l'annonce pour faire disparaître complètement le bandeau.
      </p>

      <div className="grid gap-6 rounded-2xl border border-white/10 bg-white/[0.03] p-6 md:p-8">
        <BilingualRow label="Texte du bandeau" k="banner_text" form={form} setLang={setLang} placeholder="Offre spéciale…" />
        <BilingualRow label="Titre (fenêtre)" k="modal_title" form={form} setLang={setLang} placeholder="Notre promotion" />
        <BilingualRow label="Message (fenêtre)" k="modal_body" form={form} setLang={setLang} textarea placeholder="Détails de l'annonce…" />
        <BilingualRow label="Texte du bouton" k="cta_label" form={form} setLang={setLang} placeholder="En savoir plus" />
        <div>
          <label className={lbl}>Lien du bouton</label>
          <input className={field} data-testid="ann-cta-url" value={form.cta_url} onChange={(e) => setForm((f) => ({ ...f, cta_url: e.target.value }))} placeholder="/devis ou https://…" />
        </div>

        <div className="flex items-center gap-4 pt-1">
          <button onClick={save} disabled={saving} data-testid="ann-save" className="inline-flex items-center gap-2 px-6 py-3 rounded-xl bg-accent-yellow text-ink text-sm font-semibold hover:bg-white transition-colors disabled:opacity-60">
            {saving && <Loader2 size={16} className="animate-spin" />}
            Enregistrer
          </button>
          {saved && <span className="inline-flex items-center gap-1.5 text-sm text-accent-yellow" data-testid="ann-saved"><Check size={16} /> Enregistré</span>}
        </div>
      </div>
    </div>
  );
};
